import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik, FormikProps } from 'formik';
import { Hidden } from '@material-ui/core';
import Loader from '@cpg-its/ads.common.loading-circle';
import Tooltip from '@cpg-its/ads.common.tooltip';
import { Redirect, useLocation } from 'react-router-dom';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { Institution } from '../../../../global/models/institution-model';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { FormGroup } from '../../../../global/components/layout/form-group';
import { FormField } from '../../../../global/components/form-element/form-field';
import { AddressField } from '../../../../global/components/form-element/address';
import { FormTypes, InstitutionMailingAddressFieldNames, InstitutionPhysicalAddressFieldNames } from '../../../account-info/models/form-models';
import { getAddressLabels } from '../../../account-info/selectors/form-selectors';
import { GetInstitutionDetailMailingAddressRequiredSchema } from './institution-form-model';
import { getFormatedPhone } from '../../../../global/helpers/transformer';
import { getCurrentFormattedDateNY, parseChanged, parseInstitutionPhone } from '../../../../global/helpers/form-data-helper';
import { setCurrentInstitution, setPatchInstitutionStatus } from './institution-slice';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { ApiStatuses } from '../../../../global/models/api-model';
import { patchOrganization } from '../../../../global/ducks/organization-prc-api';
import { GetInstitutionDetailValidationSchema, InstitutionState } from './institution-model';
import { useInstitutionSetting } from '../../components/use-institution-settings';
import {  TcsmStates } from '../../../../root/tcsm-model';
import { getFormattedDate } from '../../../../global/helpers/date-helpers';
import { labelSupplementStyles } from '../../../../global/constants/theme/styles/components/label-supplement-styles';
import { tcsmColors } from '../../../../global/constants/theme/theme-constants/colors';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../constants/routes';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { ContactInformation } from './component/contact-information';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { getGpsType } from '../../../../global/helpers/gps-helper';

function InstitutionDetails() {
    const app = useContext(DemographicsContext);
    const success = useSelector<TcsmStates, ApiStatuses>(state => state.institutionState.patchInstitutionStatus);
    const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps?.labelSettingState?.epiContent);
    const dispatch = useDispatch();
    const classes = labelSupplementStyles();
   const gpsItem = getGpsType(GpsType.INSTITUTION_DETAIL, app);

    const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);
    const settings = useInstitutionSetting();

    const validationSchema = GetInstitutionDetailValidationSchema(labelSettings);

    let initialInstitution = institutionState?.currentInstitution;

    const [billingAddressExpiredError, setBillingAddressExpiredError] = useState(false);
    const [physicalAddressExpiredError, setPhysicalAddressExpiredError] = useState(false);

    const billingAddressExpired = getFormattedDate(initialInstitution?.billing_address_expiration_date) < getCurrentFormattedDateNY();
    const physicalAddressExpired = getFormattedDate(initialInstitution?.physical_address_expiration_date) < getFormattedDate(new Date().toString());
    const institutionSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionSearch}`);
    const { state } = useLocation<any>();
    const apiErrors = useApiResponseError();

    useEffect(() => {
        setBillingAddressExpiredError(billingAddressExpired);
        setPhysicalAddressExpiredError(physicalAddressExpired);
    }, [institutionState]);

    if (!institutionState?.institutionClientNumber && !initialInstitution) {
        return (
            <Redirect to=
                {
                    {
                        pathname: institutionSelect,
                        state,
                    }
                }
            />);
    }

    if (!initialInstitution) {
        return (<Loader />);
    }

    initialInstitution = {
        ...initialInstitution,
        business_phone_formatted: getFormatedPhone(initialInstitution.business_phone_area_code,
            initialInstitution.business_phone_number),
        fax_phone_formatted: getFormatedPhone(initialInstitution.fax_phone_area_code, initialInstitution.fax_phone_number),
        province: initialInstitution.province,
    };

    const onSuccess = (response) => {
        dispatch((setCurrentInstitution(response?.data)));
        dispatch((setPatchInstitutionStatus(ApiStatuses.Success)));
    };

    const onSubmitHandler = (update: Institution) => {
        dispatch(apiErrorClear());
        dispatch((setPatchInstitutionStatus(ApiStatuses.None)));

        const withPhoneUpdate = parseInstitutionPhone(update);
        const toUpdate = parseChanged(initialInstitution, withPhoneUpdate);
        dispatch(patchOrganization(initialInstitution?.org_client_nbr, toUpdate, onSuccess));
    };

    const institutionLegalNameDisplay = (organizationName: string) => {
        return <div className='form-item'>
            <div className={classes.labelLayout}>
                <p className='input-label'>{labelSettings?.institutionLegalName}</p>
                <div className='supplement-wrapper'>
                    <Tooltip iconColor={tcsmColors.blue} title={labelSettings?.institutionLegalNameTooltip} />
                </div>
            </div>
            <p>{organizationName}</p>
        </div>;
    };

    const institutionProvinceDisplay = (province: string) => {
        return <div className='form-item'>
            <p className='input-label'>{labelSettings?.provinceLabel}</p>
            <p>{province}</p>
        </div>;
    };

    return (
        <>
            <Formik
                initialValues={initialInstitution}
                onSubmit={(values) => onSubmitHandler(values)}
                validationSchema={validationSchema}
                enableReinitialize
                initialErrors={apiErrors}>
                {(formProps: FormikProps<Institution>) => (
                    <Form>
                        <FormWrapper
                            formikProps={formProps}
                            submitText={labelSettings.submitText}
                            cancelText={labelSettings.cancelButtonText}
                            onCancel={() => { formProps.resetForm(); window.scrollTo(0, 0); }}
                            successfulForms={success === ApiStatuses.Success ? [FormTypes.INSTITUTION_DETAILS] : []}
                            submitDisabled={billingAddressExpiredError || physicalAddressExpiredError}
                        >
                            <PageHeader
                                headerText={settings?.institutionDetailTitle}
                                description={settings?.institutionDetailSubtitle} />
                            
                            <ContentSpacer>
                                <div dangerouslySetInnerHTML={{ __html: settings?.institutionDetailDescription }} />
                            </ContentSpacer>
                            <Hidden mdUp>
                                <SideBarContent>
                                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                </SideBarContent>
                            </Hidden>
                            <DividerSection>
                                <FormGroup>
                                    {institutionLegalNameDisplay(formProps.values.organization_name)}

                                    <FormField
                                        name='known_as'
                                        label={labelSettings?.institutionName}
                                        tooltip={{ title: labelSettings?.institutionNameTooltip }}                                        
                                        component='input'
                                        rows={4}
                                        errors={formProps.errors}
                                        touched={formProps.touched}
                                        multiline
                                        required
                                    />                                    

                                    {institutionProvinceDisplay(formProps.values.province)}
                                </FormGroup>
                            </DividerSection>

                            <DividerSection sectionText={labelSettings?.mailingAddress}>

                                <AddressField
                                    currentCountry={formProps.values.billing_country}
                                    labels={getAddressLabels(labelSettings)}
                                    addressFieldNames={InstitutionMailingAddressFieldNames}
                                    formProps={formProps}
                                    requiredSchema={GetInstitutionDetailMailingAddressRequiredSchema(formProps.values.billing_country)}
                                    addressExpired={billingAddressExpired}
                                    addressExpiredError={billingAddressExpiredError}
                                    expiredAddressChangeToggle={(hasChanged: boolean) => setBillingAddressExpiredError(!hasChanged)}
                                />
                            </DividerSection>
                            <DividerSection sectionText={labelSettings?.physicalAddress}>

                                <AddressField
                                    currentCountry={formProps.values.physical_country}
                                    labels={getAddressLabels(labelSettings)}
                                    addressFieldNames={InstitutionPhysicalAddressFieldNames}
                                    formProps={formProps}
                                    requiredSchema={GetInstitutionDetailMailingAddressRequiredSchema(formProps.values.physical_country)}
                                    addressExpired={physicalAddressExpired}
                                    addressExpiredError={physicalAddressExpiredError}
                                    expiredAddressChangeToggle={(hasChanged: boolean) => setPhysicalAddressExpiredError(!hasChanged)}
                                />
                            </DividerSection>
                            <DividerSection removeDivider sectionText={labelSettings?.contactInformation}>
                                <ContactInformation labelSettings={labelSettings} formProps={formProps} orgType={initialInstitution?.org_type} />                                
                            </DividerSection>
                        </FormWrapper>
                    </Form>
                )}
            </Formik>
        </>
    );
}

export default InstitutionDetails;